'use client'

import { ActivitySquare, Atom, BarChart2, ChartPie, ChevronLeft, Cpu, FileText, Gauge, Microscope, PencilRuler, Pyramid, Settings, Shield, TrendingUp, User, Users } from 'lucide-react'
import { usePathname, useRouter } from 'next/navigation'
import type * as React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { NavAuthButtons } from '@/components/sidebar/components/nav-auth-buttons'
import { NavMain } from '@/components/sidebar/components/nav-main'
import { NavOnboarding } from '@/components/sidebar/components/nav-onboarding'
import { NavSecondary } from '@/components/sidebar/components/nav-secondart'
import { NavUser, NavUserSkeleton } from '@/components/sidebar/components/nav-user'
import { TeamSwitcher } from '@/components/sidebar/components/team-switcher'
import { useSidebar } from '@/components/ui/sidebar'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail
} from '@/components/ui/sidebar'
import { useAuth } from '@/contexts/AuthContext'
import { useMobileSidebar } from '@/contexts/MobileSidebarContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { useActiveTeamOwner } from '@/hooks/use-active-team-owner'
import { useAdminRole } from '@/hooks/use-admin-role'
import { useBreakpoints } from '@/hooks/use-breakpoints'
import { useFeatureAccessBatch } from '@/hooks/use-feature-access-batch'
import { appendWorkspaceScopeToPath } from '@/utils/workspace-scope'

import { data } from './constanst'
import { MobileSidebar } from './mobile-sidebar'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user: authUser, isLoading: authLoading } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const { state, setOpen } = useSidebar()
  const { isSm: _isSm, isMd, isLg, isReady } = useBreakpoints()
  const { isMobileSidebarOpen, setIsMobileSidebarOpen } = useMobileSidebar()
  const { activeTeamId } = useWorkspace()
  const { canManageBilling } = useActiveTeamOwner()
  const { isAdmin } = useAdminRole()
  const { hasAccess: hasFeature } = useFeatureAccessBatch([
    'agent_profiles',
    'agent_environment',
    'projects',
    'cloud_agent_ide',
    'cloud_agent_ide_v2',
  ])
  const [section, setSection] = useState<'main' | 'sandbox' | 'settings' | 'managed-agents'>('main')
  const sidebarContentRef = useRef<HTMLDivElement>(null)
  const pushScoped = (url: string) => {
    router.push(appendWorkspaceScopeToPath(url, activeTeamId))
  }
  const pushWorkspaceSettings = () => {
    if (activeTeamId) {
      router.push(appendWorkspaceScopeToPath(`/teams/${activeTeamId}/settings`, activeTeamId))
      return
    }
    router.push('/settings')
  }
  const handleCreateAgentFromSidebar = () => {
    // Collapse the desktop sidebar when starting a new agent flow.
    setOpen(false)
    pushScoped('/agents/home')
  }

  useEffect(() => {
    const viewport = sidebarContentRef.current?.querySelector('[data-radix-scroll-area-viewport]')
    if (viewport) viewport.scrollTop = 0
  }, [section])

  const didAutoOpenForDeepLinkRef = useRef<null | 'workflows' | 'agent' | 'settings' | 'managed-agents'>(null)

  // Memoize user data to prevent unnecessary re-renders
  const userData = useMemo(() => {
    return data.user(authUser)
  }, [authUser])

  const filteredNavMain = useMemo(() => {
    return data.navMain
      .filter((item) => {
        if (!item.requiredFeatures || item.requiredFeatures.length === 0) return true
        if (item.url === '/agent/profiles') {
          return hasFeature('agent_profiles') || hasFeature('agent_environment')
        }
        return item.requiredFeatures.every((f) => hasFeature(f))
      })
  }, [hasFeature])

  // Close mobile sidebar when screen size changes to desktop
  useEffect(() => {
    if (isMd || isLg) {
      setIsMobileSidebarOpen(false)
    }
  }, [isMd, isLg, setIsMobileSidebarOpen])

  // If user deep-links into known routes, keep sidebar section coherent.
  useEffect(() => {
    const p = String(pathname || '')
    const isWorkflowsRoute = p.startsWith('/workflows') || p.startsWith('/workflow')
    const isManagedAgentsRoute = p.startsWith('/agent/profiles') || p.startsWith('/agent/environment')
    const isAgentRoute = p.startsWith('/agent') || p.startsWith('/agent-ide') || p.startsWith('/new-agent')
    const isTeamSettingsRoute = /^\/teams\/[^/]+\/(settings|members)(?:\/|$)/.test(p)
    const isUserSettingsRoute =
      p.startsWith('/settings') ||
      p.startsWith('/billing') ||
      p.startsWith('/invoices') ||
      p.startsWith('/quota') ||
      p.startsWith('/usage')

    const next: null | 'workflows' | 'agent' | 'settings' | 'managed-agents' = isManagedAgentsRoute
          ? 'managed-agents'
          : isWorkflowsRoute
          ? 'workflows'
          : isAgentRoute
            ? 'agent'
            : isTeamSettingsRoute
              ? 'settings'
            : isUserSettingsRoute
              ? 'settings'
            : null
    if (!next) {
      didAutoOpenForDeepLinkRef.current = null
      setSection('main')
      return
    }

    if (next === 'settings' || next === 'managed-agents') {
      setSection(next)
      didAutoOpenForDeepLinkRef.current = next
      return
    }

    // Workflows is a main-sidebar item; keep section on main.
    setSection('main')
    didAutoOpenForDeepLinkRef.current = 'workflows'
  }, [pathname])

  // Avoid initial mobile->desktop remount churn that can duplicate requests.
  if (!isReady) {
    return null
  }

  // Show mobile toggle button only for screens smaller than 720px (md breakpoint)
  if (!isMd) {
    return (
      <>
        {/* Mobile Sidebar */}
        <MobileSidebar 
          open={isMobileSidebarOpen} 
          onOpenChange={setIsMobileSidebarOpen} 
        />
      </>
    )
  }

  return (
    <Sidebar variant="sidebar" collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher user={userData} enableRemoteFetch={Boolean(authUser)} />
      </SidebarHeader>

      <SidebarContent ref={sidebarContentRef}>
        <div className="grid overflow-hidden">
          <div
            className={[
              'col-start-1 row-start-1 transition-all duration-200 ease-out will-change-transform',
              section === 'main' ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 pointer-events-none'
            ].join(' ')}
          >
            <NavMain
              items={filteredNavMain as any}
              onItemClick={(url) => {
                if (url === '/sandbox') {
                  setSection('sandbox')
                  return
                }
                if (url === '/agents/home') {
                  handleCreateAgentFromSidebar()
                  return
                }
                if (url === '/agent/profiles') {
                  setSection('managed-agents')
                  pushScoped('/agent/profiles')
                  return
                }
                if (url === '/settings') {
                  setSection('settings')
                  pushWorkspaceSettings()
                  return
                }
                setSection('main')
                pushScoped(url)
              }}
            />
            {isAdmin && (
              <div className="px-2">
                <SidebarMenu>
                  <SidebarMenuItem>
                    {(() => {
                      const current = String(pathname || '')
                      // Treat /admin/dispatch and /admin/kpis as their own
                      // items, not Admin.
                      const isActive =
                        current.startsWith('/admin') &&
                        !current.startsWith('/admin/dispatch') &&
                        !current.startsWith('/admin/kpis')
                      const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                      const bg = isActive ? 'bg-sidebar-accent' : ''
                      return (
                        <SidebarMenuButton
                          size="lg"
                          className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                          onClick={() => router.push('/admin')}
                        >
                          <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                            <Shield className={`!h-5 !w-5 ${color}`} />
                            {state === 'expanded' && (
                              <div className="flex items-center w-full pl-2">
                                <span className={`text-sm font-normal ${color}`}>Admin</span>
                              </div>
                            )}
                          </div>
                        </SidebarMenuButton>
                      )
                    })()}
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    {(() => {
                      const isActive = String(pathname || '').startsWith('/admin/dispatch')
                      const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                      const bg = isActive ? 'bg-sidebar-accent' : ''
                      return (
                        <SidebarMenuButton
                          size="lg"
                          className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                          onClick={() => router.push('/admin/dispatch')}
                        >
                          <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                            <ActivitySquare className={`!h-5 !w-5 ${color}`} />
                            {state === 'expanded' && (
                              <div className="flex items-center w-full pl-2">
                                <span className={`text-sm font-normal ${color}`}>Dispatch Queue</span>
                              </div>
                            )}
                          </div>
                        </SidebarMenuButton>
                      )
                    })()}
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    {(() => {
                      const isActive = String(pathname || '').startsWith('/admin/kpis')
                      const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                      const bg = isActive ? 'bg-sidebar-accent' : ''
                      return (
                        <SidebarMenuButton
                          size="lg"
                          className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                          onClick={() => router.push('/admin/kpis')}
                        >
                          <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                            <TrendingUp className={`!h-5 !w-5 ${color}`} />
                            {state === 'expanded' && (
                              <div className="flex items-center w-full pl-2">
                                <span className={`text-sm font-normal ${color}`}>KPIs</span>
                              </div>
                            )}
                          </div>
                        </SidebarMenuButton>
                      )
                    })()}
                  </SidebarMenuItem>
                </SidebarMenu>
              </div>
            )}
          </div>

          <div
            className={[
              'col-start-1 row-start-1 transition-all duration-200 ease-out will-change-transform',
              section === 'sandbox' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 pointer-events-none'
            ].join(' ')}
          >
            <div className="px-2 pt-2">
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    size="lg"
                    className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10`}
                    onClick={() => setSection('main')}
                  >
                    <div
                      className={`relative flex items-center ${
                        state === 'collapsed' ? 'justify-end w-full' : 'w-full'
                      }`}
                    >
                      <span className="!h-6 !w-6 flex items-center justify-start text-muted-foreground">
                        <ChevronLeft className="h-4 w-4 -ml-0.5" />
                      </span>
                      {state === 'expanded' && (
                        <span className="absolute left-1/2 -translate-x-1/2 text-sm font-normal text-muted-foreground">
                          {'Predict'}
                        </span>
                      )}
                    </div>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  {(() => {
                    const isActive = String(pathname || '').startsWith('/fold')
                    const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                    const bg = isActive ? 'bg-sidebar-accent' : ''
                    return (
                      <SidebarMenuButton
                        size="lg"
                        className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                        onClick={() => pushScoped('/fold/new')}
                      >
                        <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                          <Atom className={`!h-5 !w-5 ${color}`} />
                          {state === 'expanded' && (
                            <div className="flex items-center w-full pl-2">
                              <span className={`text-sm font-normal ${color}`}>Fold</span>
                            </div>
                          )}
                        </div>
                      </SidebarMenuButton>
                    )
                  })()}
                </SidebarMenuItem>

                <SidebarMenuItem>
                  {(() => {
                    const isActive = String(pathname || '').startsWith('/design')
                    const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                    const bg = isActive ? 'bg-sidebar-accent' : ''
                    return (
                      <SidebarMenuButton
                        size="lg"
                        className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                        onClick={() => pushScoped('/design/new')}
                      >
                        <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                          <PencilRuler className={`!h-5 !w-5 ${color}`} />
                          {state === 'expanded' && (
                            <div className="flex items-center w-full pl-2">
                              <span className={`text-sm font-normal ${color}`}>Design</span>
                            </div>
                          )}
                        </div>
                      </SidebarMenuButton>
                    )
                  })()}
                </SidebarMenuItem>

                <SidebarMenuItem>
                  {(() => {
                    const isActive = String(pathname || '').startsWith('/properties')
                    const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                    const bg = isActive ? 'bg-sidebar-accent' : ''
                    return (
                      <SidebarMenuButton
                        size="lg"
                        className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                        onClick={() => pushScoped('/properties/new')}
                      >
                        <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                          <Microscope className={`!h-5 !w-5 ${color}`} />
                          {state === 'expanded' && (
                            <div className="flex items-center w-full pl-2">
                              <span className={`text-sm font-normal ${color}`}>Properties</span>
                            </div>
                          )}
                        </div>
                      </SidebarMenuButton>
                    )
                  })()}
                </SidebarMenuItem>

                {/* Workflows moved to main sidebar */}
              </SidebarMenu>
            </div>
          </div>

          <div
            className={[
              'col-start-1 row-start-1 transition-all duration-200 ease-out will-change-transform',
              section === 'managed-agents' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 pointer-events-none'
            ].join(' ')}
          >
            <div className="px-2 pt-2">
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    size="lg"
                    className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10`}
                    onClick={() => setSection('main')}
                  >
                    <div
                      className={`relative flex items-center ${
                        state === 'collapsed' ? 'justify-end w-full' : 'w-full'
                      }`}
                    >
                      <span className="!h-6 !w-6 flex items-center justify-start text-muted-foreground">
                        <ChevronLeft className="h-4 w-4 -ml-0.5" />
                      </span>
                      {state === 'expanded' && (
                        <span className="absolute left-1/2 -translate-x-1/2 text-sm font-normal text-muted-foreground">
                          {'Agents'}
                        </span>
                      )}
                    </div>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                {hasFeature('agent_profiles') && (
                <SidebarMenuItem>
                  {(() => {
                    const isActive = String(pathname || '').startsWith('/agent/profiles')
                    const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                    const bg = isActive ? 'bg-sidebar-accent' : ''
                    return (
                      <SidebarMenuButton
                        size="lg"
                        className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                        onClick={() => pushScoped('/agent/profiles')}
                      >
                        <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                          <Pyramid className={`!h-5 !w-5 ${color}`} />
                          {state === 'expanded' && (
                            <div className="flex items-center w-full pl-2">
                              <span className={`text-sm font-normal ${color}`}>Agents</span>
                            </div>
                          )}
                        </div>
                      </SidebarMenuButton>
                    )
                  })()}
                </SidebarMenuItem>
                )}

                {hasFeature('agent_environment') && (
                <SidebarMenuItem>
                  {(() => {
                    const isActive = String(pathname || '').startsWith('/agent/environment')
                    const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                    const bg = isActive ? 'bg-sidebar-accent' : ''
                    return (
                      <SidebarMenuButton
                        size="lg"
                        className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                        onClick={() => pushScoped('/agent/environment')}
                      >
                        <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                          <Cpu className={`!h-5 !w-5 ${color}`} />
                          {state === 'expanded' && (
                            <div className="flex items-center w-full pl-2">
                              <span className={`text-sm font-normal ${color}`}>Environment</span>
                            </div>
                          )}
                        </div>
                      </SidebarMenuButton>
                    )
                  })()}
                </SidebarMenuItem>
                )}
              </SidebarMenu>
            </div>
          </div>

          <div
            className={[
              'col-start-1 row-start-1 transition-all duration-200 ease-out will-change-transform',
              section === 'settings' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 pointer-events-none'
            ].join(' ')}
          >
            <div className="px-2 pt-2">
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    size="lg"
                    className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10`}
                    onClick={() => setSection('main')}
                  >
                    <div
                      className={`relative flex items-center ${
                        state === 'collapsed' ? 'justify-end w-full' : 'w-full'
                      }`}
                    >
                      <span className="!h-6 !w-6 flex items-center justify-start text-muted-foreground">
                        <ChevronLeft className="h-4 w-4 -ml-0.5" />
                      </span>
                      {state === 'expanded' && (
                        <span className="absolute left-1/2 -translate-x-1/2 text-sm font-normal text-muted-foreground">
                          {'Settings'}
                        </span>
                      )}
                    </div>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  {(() => {
                    const hasTeamWorkspace = Boolean(activeTeamId)
                    const currentPath = String(pathname || '')
                    const isActive = hasTeamWorkspace
                      ? /^\/teams\/[^/]+\/settings(?:\/|$)/.test(currentPath)
                      : currentPath === '/settings'
                    const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                    const bg = isActive ? 'bg-sidebar-accent' : ''
                    return (
                      <SidebarMenuButton
                        size="lg"
                        className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                        onClick={pushWorkspaceSettings}
                      >
                        <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                          {hasTeamWorkspace ? (
                            <Settings className={`!h-5 !w-5 ${color}`} />
                          ) : (
                            <User className={`!h-5 !w-5 ${color}`} />
                          )}
                          {state === 'expanded' && (
                            <div className="flex items-center w-full pl-2">
                              <span className={`text-sm font-normal ${color}`}>
                                {hasTeamWorkspace ? 'Settings' : 'Profile'}
                              </span>
                            </div>
                          )}
                        </div>
                      </SidebarMenuButton>
                    )
                  })()}
                </SidebarMenuItem>

                {activeTeamId ? (
                  <SidebarMenuItem>
                    {(() => {
                      const isActive = /^\/teams\/[^/]+\/members(?:\/|$)/.test(String(pathname || ''))
                      const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                      const bg = isActive ? 'bg-sidebar-accent' : ''
                      return (
                        <SidebarMenuButton
                          size="lg"
                          className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                          onClick={() => pushScoped(`/teams/${activeTeamId}/members`)}
                        >
                          <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                            <Users className={`!h-5 !w-5 ${color}`} />
                            {state === 'expanded' && (
                              <div className="flex items-center w-full pl-2">
                                <span className={`text-sm font-normal ${color}`}>Members</span>
                              </div>
                            )}
                          </div>
                        </SidebarMenuButton>
                      )
                    })()}
                  </SidebarMenuItem>
                ) : null}
                {canManageBilling ? (
                  <>
                    <SidebarMenuItem>
                      {(() => {
                        const isActive = String(pathname || '').startsWith('/billing')
                        const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                        const bg = isActive ? 'bg-sidebar-accent' : ''
                        return (
                          <SidebarMenuButton
                            size="lg"
                            className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                            onClick={() => pushScoped('/billing')}
                          >
                            <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                              <BarChart2 className={`!h-5 !w-5 ${color}`} />
                              {state === 'expanded' && (
                                <div className="flex items-center w-full pl-2">
                                  <span className={`text-sm font-normal ${color}`}>Billing</span>
                                </div>
                              )}
                            </div>
                          </SidebarMenuButton>
                        )
                      })()}
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      {(() => {
                        const isActive = String(pathname || '').startsWith('/invoices')
                        const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                        const bg = isActive ? 'bg-sidebar-accent' : ''
                        return (
                          <SidebarMenuButton
                            size="lg"
                            className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                            onClick={() => pushScoped('/invoices')}
                          >
                            <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                              <FileText className={`!h-5 !w-5 ${color}`} />
                              {state === 'expanded' && (
                                <div className="flex items-center w-full pl-2">
                                  <span className={`text-sm font-normal ${color}`}>Invoices</span>
                                </div>
                              )}
                            </div>
                          </SidebarMenuButton>
                        )
                      })()}
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      {(() => {
                        const isActive = String(pathname || '').startsWith('/quota')
                        const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                        const bg = isActive ? 'bg-sidebar-accent' : ''
                        return (
                          <SidebarMenuButton
                            size="lg"
                            className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                            onClick={() => pushScoped('/quota')}
                          >
                            <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                              <Gauge className={`!h-5 !w-5 ${color}`} />
                              {state === 'expanded' && (
                                <div className="flex items-center w-full pl-2">
                                  <span className={`text-sm font-normal ${color}`}>Quotas</span>
                                </div>
                              )}
                            </div>
                          </SidebarMenuButton>
                        )
                      })()}
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      {(() => {
                        const isActive = String(pathname || '').startsWith('/usage')
                        const color = isActive ? 'text-foreground' : 'text-muted-foreground'
                        const bg = isActive ? 'bg-sidebar-accent' : ''
                        return (
                          <SidebarMenuButton
                            size="lg"
                            className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${bg}`}
                            onClick={() => pushScoped('/usage')}
                          >
                            <div className={`flex items-center ${state === 'collapsed' ? 'justify-end w-full' : ''}`}>
                              <ChartPie className={`!h-5 !w-5 ${color}`} />
                              {state === 'expanded' && (
                                <div className="flex items-center w-full pl-2">
                                  <span className={`text-sm font-normal ${color}`}>Usage</span>
                                </div>
                              )}
                            </div>
                          </SidebarMenuButton>
                        )
                      })()}
                    </SidebarMenuItem>
                  </>
                ) : null}
              </SidebarMenu>
            </div>
          </div>
        </div>
      </SidebarContent>

      <SidebarFooter>
        <NavSecondary items={data.navSecondary} noGroup />
        {authUser && <NavOnboarding />}
        {authLoading ? <NavUserSkeleton /> : authUser ? <NavUser user={userData} /> : <NavAuthButtons />}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}

// Export the MobileSidebar component
export { MobileSidebar }
